import { useInfiniteQuery } from '@tanstack/react-query';

import {
  FETCH_ALL_CONVERSATIONS_PAGE_SIZE,
  searchConversation,
} from '../services/conversations-service';
import { useChat } from '../use-chat/useChat';

const QUERY_SEARCH_CONVERSATION = 'QUERY_SEARCH_CONVERSATION';
export const QUERY_ALL_CONVERSATIONS = 'QUERY_ALL_CONVERSATIONS';

export interface UseFetchInfiniteConversationProps {
  queryString: string;
  shouldFetch?: boolean;
}

export const useFetchInfiniteSearchConversation = ({
  queryString,
  shouldFetch = true,
}: UseFetchInfiniteConversationProps) => {
  const { data, isLoading, hasNextPage, fetchNextPage, isRefetching } =
    useInfiniteQuery({
      queryKey: [QUERY_SEARCH_CONVERSATION, queryString],
      queryFn: async ({ pageParam }) => {
        const response = await searchConversation(queryString, pageParam);

        if (!response?.results) {
          return [];
        }

        return response.results;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage?.length === 0) {
          return undefined;
        }

        return lastPageParam + 1;
      },
      enabled: shouldFetch,
    });

  return {
    data,
    isLoading: isLoading || isRefetching,
    hasNextPage,
    fetchNextPage,
  };
};

export const useFetchInfiniteAllConversations = () => {
  const { data, isLoading, hasNextPage, isRefetching, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [QUERY_ALL_CONVERSATIONS],
      queryFn: ({ pageParam }) => {
        return useChat.getState().getConversations(pageParam);
      },
      refetchOnMount: 'always',
      initialPageParam: 1,
      getNextPageParam: (lastPage, _, lastPageParam) => {
        if (lastPage.length >= FETCH_ALL_CONVERSATIONS_PAGE_SIZE) {
          return lastPageParam + 1;
        }

        return undefined;
      },
    });

  return {
    data,
    isLoading: isLoading || isRefetching,
    hasNextPage,
    fetchNextPage,
  };
};
